import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import * as m from '../../../../localization/out/messages.js';
import { getResumeFileByID } from '../../../api/resume-file.js';
import { getAndEnsureUserDataOfUserID } from '../../../api/user.js';
import { ResumeFile } from '../../../db/ResumeFile.js';
import { ResumeReviewFile } from '../../../db/ResumeReviewFile.js';
import { cvReviewUsageQuery } from '../../../features/cv-review/api/cvReviewUsageQuery.js';
import { reviewCvFileQuery } from '../../../features/cv-review/api/reviewCvFileQuery.js';
import { CtaPillButton } from '../../components/cta-pill-button.js';
import { useScrollToError } from '../../components/format-form-error.js';
import { ScoreInfoButton, ScoreInfoDescription } from '../../components/score-info-toggle.js';

export const RegisterOnboardingKiFeedback = stateful(
  ({
    onFinished,
    onBack,
    onChangeProfile,
  }: {
    onFinished: () => void;
    onBack: () => void;
    onChangeProfile: () => void;
  }) => {
    const [kiFeedback, setKiFeedback] = useState<true | false | null>(null);
    const [showKIFeedback, setShowKIFeedback] = useState(false);
    const [kiFeedbackError, setKiFeedbackError] = useState<string | null>(null, []);
    // Picked-but-not-yet-uploaded replacement CV, plus the state of the upload it triggers.
    const [pickedFile, setPickedFile] = useState<File | null>(null, []);
    const [uploading, setUploading] = useState(false, []);
    const [actionError, setActionError] = useState<string | null>(null, []);
    // Independent expand/collapse state for the ⓘ rating-scale definitions.
    const [completenessInfoExpanded, setCompletenessInfoExpanded] = useState(false);
    const [structureInfoExpanded, setStructureInfoExpanded] = useState(false);
    const [contentInfoExpanded, setContentInfoExpanded] = useState(false);
    const [clarityInfoExpanded, setClarityInfoExpanded] = useState(false);
    // Both banners live in mutually exclusive branches of the template, but name the
    // anchors anyway so neither can ever scroll to the other one.
    useScrollToError(kiFeedbackError, 'ki-feedback-choice');
    useScrollToError(actionError, 'ki-feedback-review');

    const user = useObservable(() => currentUser(), []);
    const userData = useQuery(
      () => (user?._id ? getAndEnsureUserDataOfUserID(user?._id)({ _id: 1, resumeFileId: 1 }) : of(null)),
      [user?._id],
    );

    const [count, setCount] = useState(0);
    const triggerUpdate = () => setCount(count + 1);
    const reviewResultsQuery = useQuery(
      () =>
        showKIFeedback && userData.value?.resumeFileId ? reviewCvFileQuery(userData.value.resumeFileId)() : of(null),
      [showKIFeedback, userData.value?.resumeFileId, count],
    );

    const reviewDetailsQuery = useQuery(
      () =>
        showKIFeedback && userData.value?.resumeFileId
          ? getResumeFileByID(userData.value.resumeFileId)({
              _id: 1,
              reviewResultFileId: 1,
              reviewResult: {
                overallScore: 1,
                completenessScore: 1,
                structureScore: 1,
                contentScore: 1,
                clarityScore: 1,
                summary: 1,
              },
            })
          : of(null),
      [showKIFeedback, userData.value?.resumeFileId, reviewResultsQuery.value],
    );

    // Refetched after every completed review so the badge reflects the quota just spent.
    const usageQuery = useQuery(
      () => (showKIFeedback ? cvReviewUsageQuery()({ used: 1, remaining: 1, limit: 1 }) : of(null)),
      [showKIFeedback, reviewResultsQuery.value],
    );
    const hasReviewsLeft = (usageQuery.value?.remaining ?? 0) > 0;

    // `uploading` counts as loading so the "next steps" section unmounts during the upload.
    // That also resets FileInput's internal pending-file state, which we can't clear from here.
    const loading = reviewResultsQuery.loading || reviewDetailsQuery.loading || uploading;

    // The review itself is the only thing that can fail out of band; `userData.save()` and the
    // upload are reported through `actionError`.
    const reviewError = actionError ?? reviewResultsQuery.error?.message ?? reviewDetailsQuery.error?.message ?? null;

    const uploadNewCv = async () => {
      if (!pickedFile || !userData.value) return;
      setActionError(null);
      setUploading(true);
      try {
        // FileInput only uploads through a generated form's `uploadAllPendingFiles()`; this
        // input is standalone, so upload explicitly and assign the id only once it succeeded.
        const fileID = await ResumeFile.upload(pickedFile);
        userData.value.resumeFileId = fileID;
        await userData.value.save();
        setPickedFile(null);
        triggerUpdate();
      } catch (e: any) {
        setActionError(e?.message || m.cv_check_error());
      } finally {
        setUploading(false);
      }
    };

    return showKIFeedback
      ? html`
          <div class="space-y-6">
            <h3 class="card-title">${m.register_onboarding_ki_feedback_title()}</h3>
            ${
              reviewError
                ? html`<div class="alert alert-error" data-error-anchor="ki-feedback-review">
                    <span class="icon-[material-symbols--error]"></span>
                    <span>${reviewError}</span>
                  </div>`
                : nothing
            }
            ${
              loading
                ? html`
                    <div class="flex items-center gap-3">
                      <span class="loading loading-spinner loading-md"></span>
                      <span class="text-sm">${uploading ? m.file_input_uploading() : m.ki_feedback_loading()}</span>
                    </div>
                    <div class="alert alert-info mt-3">
                      <span class="text-sm">${m.ki_feedback_loading_hint()}</span>
                    </div>
                  `
                : reviewDetailsQuery.value?.reviewResult
                  ? html`
                      <div class="space-y-4">
                        <div class="stats shadow w-full">
                          <div class="stat">
                            <div class="stat-title">${m.overall_rating()}</div>
                            <div class="stat-value text-primary">
                              ${Math.round((reviewDetailsQuery.value.reviewResult.overallScore || 0) * 10) / 10}/10
                            </div>
                          </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div class="flex justify-between text-sm mb-1">
                              <span class="inline-flex items-center gap-1"
                                >${m.score_completeness()}${ScoreInfoButton({
                                  label: m.score_completeness(),
                                  expanded: completenessInfoExpanded,
                                  onToggle: () => setCompletenessInfoExpanded(!completenessInfoExpanded),
                                })}</span
                              ><span> ${reviewDetailsQuery.value.reviewResult.completenessScore ?? '-'} </span>
                            </div>
                            <progress
                              class="progress progress-primary w-full"
                              max="10"
                              value="${reviewDetailsQuery.value.reviewResult.completenessScore ?? 0}"
                            ></progress>
                            ${
                              completenessInfoExpanded
                                ? ScoreInfoDescription({ description: m.score_completeness_description() })
                                : nothing
                            }
                          </div>
                          <div>
                            <div class="flex justify-between text-sm mb-1">
                              <span class="inline-flex items-center gap-1"
                                >${m.score_structure()}${ScoreInfoButton({
                                  label: m.score_structure(),
                                  expanded: structureInfoExpanded,
                                  onToggle: () => setStructureInfoExpanded(!structureInfoExpanded),
                                })}</span
                              >
                              <span> ${reviewDetailsQuery.value.reviewResult.structureScore ?? '-'} </span>
                            </div>
                            <progress
                              class="progress progress-primary w-full"
                              max="10"
                              value="${reviewDetailsQuery.value.reviewResult.structureScore ?? 0}"
                            ></progress>
                            ${
                              structureInfoExpanded
                                ? ScoreInfoDescription({ description: m.score_structure_description() })
                                : nothing
                            }
                          </div>
                          <div>
                            <div class="flex justify-between text-sm mb-1">
                              <span class="inline-flex items-center gap-1"
                                >${m.score_content()}${ScoreInfoButton({
                                  label: m.score_content(),
                                  expanded: contentInfoExpanded,
                                  onToggle: () => setContentInfoExpanded(!contentInfoExpanded),
                                })}</span
                              ><span> ${reviewDetailsQuery.value.reviewResult.contentScore ?? '-'} </span>
                            </div>
                            <progress
                              class="progress progress-primary w-full"
                              max="10"
                              value="${reviewDetailsQuery.value.reviewResult.contentScore ?? 0}"
                            ></progress>
                            ${
                              contentInfoExpanded
                                ? ScoreInfoDescription({ description: m.score_content_description() })
                                : nothing
                            }
                          </div>
                          <div>
                            <div class="flex justify-between text-sm mb-1">
                              <span class="inline-flex items-center gap-1"
                                >${m.score_clarity()}${ScoreInfoButton({
                                  label: m.score_clarity(),
                                  expanded: clarityInfoExpanded,
                                  onToggle: () => setClarityInfoExpanded(!clarityInfoExpanded),
                                })}</span
                              ><span> ${reviewDetailsQuery.value.reviewResult.clarityScore ?? '-'} </span>
                            </div>
                            <progress
                              class="progress progress-primary w-full"
                              max="10"
                              value="${reviewDetailsQuery.value.reviewResult.clarityScore ?? 0}"
                            ></progress>
                            ${
                              clarityInfoExpanded
                                ? ScoreInfoDescription({ description: m.score_clarity_description() })
                                : nothing
                            }
                          </div>
                        </div>

                        ${
                          reviewDetailsQuery.value.reviewResult.summary
                            ? html`
                                <div class="prose text-sm max-w-prose">
                                  <div class="font-bold">${m.summary()}</div>
                                  ${reviewDetailsQuery.value.reviewResult.summary
                                    .split('\n')
                                    .map(line => html`<p>${line}</p>`)}
                                </div>
                              `
                            : html``
                        }
                        ${
                          reviewDetailsQuery.value.reviewResultFileId
                            ? html`
                                <a
                                  href="${ResumeReviewFile.getServeLink(reviewDetailsQuery.value.reviewResultFileId)}"
                                  target="_blank"
                                  class="block my-4 no-underline hover:no-underline text-base-content"
                                >
                                  <div
                                    class="flex items-center justify-between p-4 border-2 border-dashed border-primary rounded-lg bg-base-100 shadow-lg transition-shadow hover:shadow-2xl cursor-pointer"
                                  >
                                    <div class="flex items-center gap-4 w-full">
                                      <!-- Document preview shim -->
                                      <div
                                        class="relative w-20 h-24 md:w-24 md:h-28 flex-shrink-0 bg-base-200 rounded-md shadow-md overflow-hidden"
                                      >
                                        <!-- PDF icon overlay -->
                                        <div
                                          class="absolute inset-0 flex flex-col items-center justify-center bg-base-100 bg-opacity-90"
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="h-10 w-10 text-primary"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                          >
                                            <path
                                              stroke-linecap="round"
                                              stroke-linejoin="round"
                                              stroke-width="1.5"
                                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                            />
                                          </svg>
                                          <div class="text-center mt-1 font-medium text-primary text-sm">
                                            ${m.register_onboarding_pdf_label()}
                                          </div>
                                          <div class="text-xs text-center mt-0.5 text-base-content opacity-70">
                                            ${m['annotated_resume']?.() || 'Annotated Resume'}
                                          </div>
                                        </div>
                                        <!-- Decorative annotations -->
                                        <div class="absolute top-2 left-2 w-8 h-1 bg-primary opacity-30 rounded"></div>
                                        <div class="absolute top-5 left-3 w-6 h-1 bg-primary opacity-30 rounded"></div>
                                        <div class="absolute top-8 left-2 w-9 h-1 bg-primary opacity-30 rounded"></div>
                                        <div class="absolute top-12 right-3 w-7 h-1 bg-accent opacity-30 rounded"></div>
                                        <div
                                          class="absolute bottom-3 left-4 w-8 h-1 bg-accent opacity-30 rounded"
                                        ></div>
                                      </div>

                                      <!-- Text and description -->
                                      <div class="flex-1">
                                        <h3 class="font-bold text-lg">
                                          ${m['review_annotations_title']?.() || 'Review Annotations'}
                                        </h3>
                                        <p class="text-sm opacity-80">
                                          ${
                                            m['review_annotations_description']?.() ||
                                            'View detailed feedback with annotations directly on your resume'
                                          }
                                        </p>
                                        <p class="text-sm text-warning">${m.review_annotations_warning()}</p>
                                      </div>

                                      <!-- Button -->
                                      <div class="btn btn-primary flex-shrink-0">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          class="h-5 w-5 mr-2"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                          />
                                          <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                          />
                                        </svg>
                                        ${m.open_annotated_pdf()}
                                      </div>
                                    </div>
                                  </div>
                                </a>
                              `
                            : html``
                        }
                      </div>
                    `
                  : html`
                      <div class="alert alert-warning">
                        <span class="text-sm">${m.no_results_found()}</span>
                      </div>
                    `
            }
            ${
              loading
                ? nothing
                : html`
                    <div class="divider mt-8 mb-6">${m['next_steps']?.() || 'Next Steps'}</div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <!-- Upload new CV card -->
                      <div class="card bg-base-100 shadow-md transition-shadow border border-base-300">
                        <div class="card-body">
                          <h3 class="card-title text-base">${m.ask_upload_new_cv_after_feedback()}</h3>
                          ${
                            hasReviewsLeft
                              ? html`
                                  <div class="badge badge-primary badge-sm">
                                    ${m.cv_check_remaining_reviews({
                                      remaining: String(usageQuery.value!.remaining),
                                      limit: String(usageQuery.value!.limit),
                                    })}
                                  </div>
                                  <div class="mt-3 space-y-3">
                                    ${FileInput({
                                      accept: 'application/pdf,.pdf',
                                      fileEntity: ResumeFile,
                                      value: null,
                                      clearable: true,
                                      disabled: loading,
                                      placeholder: m.cv_check_upload_label(),
                                      onPicked: value => {
                                        if (value === null) setPickedFile(null);
                                      },
                                      onFilesChanged: ({ pendingFiles }) => setPickedFile(pendingFiles[0] ?? null),
                                    })}
                                    <div class="card-actions justify-end">
                                      <button
                                        class="btn btn-primary ${!pickedFile || loading ? 'btn-disabled' : ''}"
                                        ?disabled=${!pickedFile || loading}
                                        @click=${uploadNewCv}
                                      >
                                        ${m.cv_check_new_review()}
                                      </button>
                                    </div>
                                  </div>
                                `
                              : usageQuery.value
                                ? html`
                                    <div class="alert alert-warning mt-3">
                                      <span class="text-sm">
                                        ${m.cv_check_no_reviews_left({ limit: String(usageQuery.value.limit) })}
                                      </span>
                                    </div>
                                  `
                                : nothing
                          }
                        </div>
                      </div>

                      <!-- Update profile card -->
                      <div class="card bg-base-100 shadow-md transition-shadow border border-base-300">
                        <div class="card-body">
                          <h3 class="card-title text-base">${m.ask_update_profile_after_feedback()}</h3>
                          <div class="card-actions justify-end mt-3">
                            <button class="btn ${loading ? 'btn-disabled' : ''}" @click=${() => onChangeProfile()}>
                              ${m['change_profile']?.()}
                              <span class="icon-[material-symbols--arrow-forward] ml-1"></span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="mt-8 flex justify-between gap-4">
                      <button class="btn" @click=${() => onBack()}>${m.back()}</button>
                      ${CtaPillButton({
                        label: m.next(),
                        onClick: () => onFinished(),
                      })}
                    </div>
                  `
            }
          </div>
        `
      : html`
          <div class="space-y-4">
            <h3 class="card-title">${m.ai_feedback_question_title()}<span class="text-error"> *</span></h3>
            <div class="prose text-sm opacity-90">
              <p>${m.ai_feedback_question_p1()}</p>
              <p class="alert alert-warning not-prose p-3 rounded-md mt-2">
                <span class="font-medium">${m.warning_attention()}</span>
                ${m.matching_score_hint_text()}
              </p>
            </div>

            ${
              kiFeedbackError
                ? html`<div class="alert alert-error" data-error-anchor="ki-feedback-choice">
                    <span class="icon-[material-symbols--error]"></span>
                    <span>${kiFeedbackError}</span>
                  </div>`
                : nothing
            }
            <div class="form-control">
              <label class="label cursor-pointer items-start gap-3">
                <input
                  type="radio"
                  name="ki-feedback-choice"
                  class="radio mt-1"
                  @change=${() => {
                    setKiFeedback(true);
                    setKiFeedbackError(null);
                  }}
                />
                <span class="label-text whitespace-normal">
                  ${m.ai_feedback_yes_option_part1()}
                  <a class="link" href="/privacy" target="_blank" rel="noopener">${m.privacy_policy()}</a>
                  ${m.ai_feedback_yes_option_after_link()}
                </span>
              </label>
            </div>

            <div class="form-control">
              <label class="label cursor-pointer items-start gap-3">
                <input
                  type="radio"
                  name="ki-feedback-choice"
                  class="radio mt-1"
                  @change=${() => {
                    setKiFeedback(false);
                    setKiFeedbackError(null);
                  }}
                />
                <span class="label-text whitespace-normal">${m.no_ai_feedback_option_text()}</span>
              </label>
            </div>

            <div class="mt-6 flex flex-col sm:flex-row items-center sm:items-start justify-between gap-2">
              <button
                class="btn w-full sm:w-auto ${kiFeedback === null || loading ? 'btn-disabled' : ''}"
                @click=${() => onBack()}
              >
                ${m.back()}
              </button>
              ${CtaPillButton({
                label: m.next(),
                disabled: loading,
                onClick: () => {
                  if (kiFeedback === null) {
                    setKiFeedbackError(m.register_onboarding_ki_feedback_error());
                    return;
                  }
                  setKiFeedbackError(null);
                  if (kiFeedback === true) {
                    setShowKIFeedback(true);
                  } else {
                    onFinished();
                  }
                },
              })}
            </div>
          </div>
        `;
  },
);
